/**
 * TCR — Rendu de la page /pages/favoris.
 *
 * Les favoris ne sont pas connus de Liquid (ils vivent en base ou en
 * localStorage), la page est donc rendue côté client. Les données produit sont
 * récupérées via /products/{handle}.js.
 *
 * ⚠️ Le storefront pénalise les rafales sur /products/*.js (~27 requêtes puis
 * 429). Un client avec 40 favoris déclencherait la limite : on plafonne donc la
 * concurrence et on retente les 429 avec un back-off. Un 429 n'est PAS un 404,
 * il ne doit jamais faire disparaître un produit de la liste.
 */
(function () {
  'use strict';

  var cfg = window.tcrWishlistConfig || {};
  var CONCURRENCY = 5;
  var productCache = {};

  /* Le filtre `t` de Shopify échappe les traductions en entités HTML :
     "n'avez" arrive en "n&#39;avez". Comme on injecte via textContent (et non
     innerHTML, pour ne pas ouvrir une injection), il faut décoder d'abord —
     sinon l'entité s'affiche littéralement. */
  function decodeEntities(s) {
    if (typeof s !== 'string' || s.indexOf('&') === -1) return s;
    var ta = document.createElement('textarea');
    ta.innerHTML = s;
    return ta.value;
  }

  function t(key, fallback) {
    return decodeEntities((cfg.strings && cfg.strings[key]) || fallback);
  }

  function money(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr', {
        style: 'currency',
        currency: cfg.currency || 'EUR',
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' €';
    }
  }

  // ─── Récupération produit, throttlée ───────────────────────────────────────

  function fetchProduct(handle, attempt) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    attempt = attempt || 0;

    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' },
    })
      .then(function (r) {
        if (r.status === 429) {
          // Pénalité de burst : on retente, on ne conclut PAS à une disparition.
          if (attempt >= 3) throw new Error('rate-limited');
          var wait = 800 * Math.pow(2, attempt);
          return new Promise(function (res) {
            setTimeout(function () {
              res(fetchProduct(handle, attempt + 1));
            }, wait);
          });
        }
        if (r.status === 404) return null; // produit réellement supprimé
        if (!r.ok) throw new Error('http-' + r.status);
        return r.json();
      })
      .then(function (p) {
        if (p && p.handle) productCache[handle] = p;
        return p;
      });
  }

  /** Exécute les tâches par vagues de `limit`, pour ne pas saturer le storefront. */
  function throttled(items, limit, worker) {
    var results = new Array(items.length);
    var cursor = 0;

    function next() {
      if (cursor >= items.length) return Promise.resolve();
      var i = cursor++;
      return worker(items[i], i)
        .catch(function () {
          return null;
        })
        .then(function (r) {
          results[i] = r;
          return next();
        });
    }

    var runners = [];
    for (var k = 0; k < Math.min(limit, items.length); k++) runners.push(next());
    return Promise.all(runners).then(function () {
      return results;
    });
  }

  // ─── Rendu ─────────────────────────────────────────────────────────────────

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function btn(cls, label, onClick) {
    var b = el('button', cls, label);
    b.type = 'button';
    b.addEventListener('click', onClick);
    return b;
  }

  /* Une liste "virtuelle" (id null) n'existe pas encore en base : elle est
     renvoyée par le backend tant que le client n'a rien mis en favori. Aucune
     action de gestion ne s'y applique. La liste 'local' est celle du visiteur
     non connecté, stockée en localStorage. */
  function isVirtual(list) {
    return !list.id || list.id === 'local';
  }

  function renderItem(item, product, allLists) {
    var row = el('li', 'wishlist-page__item');
    row.setAttribute('data-variant', item.variantId);

    var variant = null;
    if (product && product.variants) {
      var numericId = String(item.variantId).split('/').pop();
      for (var i = 0; i < product.variants.length; i++) {
        if (String(product.variants[i].id) === numericId) {
          variant = product.variants[i];
          break;
        }
      }
    }

    var link = el('a', 'wishlist-page__link');
    link.href = '/products/' + item.handle;

    if (product && product.featured_image) {
      var img = el('img', 'wishlist-page__img');
      img.src = product.featured_image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2');
      img.alt = product.title || '';
      img.loading = 'lazy';
      img.width = 100;
      img.height = 100;
      link.appendChild(img);
    }

    var body = el('div', 'wishlist-page__body');
    body.appendChild(el('span', 'wishlist-page__title', product ? product.title : item.handle));

    if (variant) {
      body.appendChild(el('span', 'wishlist-page__price', money(variant.price)));
      if (!variant.available) {
        body.appendChild(el('span', 'wishlist-page__oos', t('soldOut', 'Épuisé')));
      }
    } else if (!product) {
      // Produit introuvable : on le garde visible et on le signale, plutôt que
      // de le supprimer silencieusement de la liste du client.
      body.appendChild(el('span', 'wishlist-page__oos', t('unavailable', 'Indisponible')));
    }

    link.appendChild(body);
    row.appendChild(link);

    // Déplacement vers une autre liste. Un <select> natif plutôt qu'un
    // glisser-déposer : utilisable au doigt et accessible au clavier.
    var others = (allLists || []).filter(function (l) {
      return !isVirtual(l) && l.id !== item.listId;
    });
    if (others.length && !isVirtual({ id: item.listId })) {
      var sel = el('select', 'wishlist-page__move');
      var ph = el('option', null, t('moveTo', 'Déplacer vers…'));
      ph.value = '';
      sel.appendChild(ph);
      others.forEach(function (l) {
        var o = el('option', null, l.name);
        o.value = l.id;
        sel.appendChild(o);
      });
      sel.addEventListener('change', function () {
        if (!sel.value) return;
        sel.disabled = true;
        window.TCRWishlist.moveItem(item.variantId, item.listId, sel.value)
          .then(render)
          .catch(function () {
            sel.disabled = false;
            sel.value = '';
          });
      });
      row.appendChild(sel);
    }

    row.appendChild(
      btn('wishlist-page__remove', t('remove', 'Retirer'), function (e) {
        e.currentTarget.disabled = true;
        window.TCRWishlist.remove(item.variantId, item.listId).then(render).catch(function () {
          e.currentTarget.disabled = false;
        });
      })
    );

    return row;
  }

  function renderList(list, root, allLists) {
    var block = el('section', 'wishlist-page__list');

    var head = el('header', 'wishlist-page__list-head');
    head.appendChild(el('h2', 'wishlist-page__list-name', list.name));

    // Actions de gestion : indisponibles sur une liste virtuelle ou locale.
    if (!isVirtual(list)) {
      var actions = el('div', 'wishlist-page__list-actions');

      actions.appendChild(
        btn('wishlist-page__action', t('renameList', 'Renommer'), function () {
          var name = window.prompt(t('listName', 'Nom de la liste'), list.name);
          if (name && name.trim() && name !== list.name) {
            window.TCRWishlist.renameList(list.id, name.trim()).then(render);
          }
        })
      );

      // La liste par défaut n'est pas supprimable : elle sert de cible au cœur.
      if (!list.isDefault) {
        actions.appendChild(
          btn('wishlist-page__action wishlist-page__action--danger', t('deleteList', 'Supprimer'), function () {
            if (window.confirm(t('deleteConfirm', 'Supprimer cette liste et son contenu ?'))) {
              window.TCRWishlist.deleteList(list.id).then(render);
            }
          })
        );
      }
      head.appendChild(actions);
    }
    block.appendChild(head);

    if (!list.items || list.items.length === 0) {
      block.appendChild(el('p', 'wishlist-page__empty', t('empty', 'Aucun favori.')));
      root.appendChild(block);
      return Promise.resolve();
    }

    var ul = el('ul', 'wishlist-page__items');
    block.appendChild(ul);

    // Le devis est réservé aux professionnels (tag `pro` ou `pro-pending`).
    // Les autres clients connectés voient une invitation à s'inscrire pro —
    // c'est la seule porte d'entrée du tunnel depuis cette décision.
    if (cfg.loggedIn && list.id !== 'local' && !cfg.isPro) {
      var proLink = el('a', 'wishlist-page__pro-link', t('proPrompt', 'Vous êtes professionnel ?'));
      proLink.href = cfg.registerProUrl || '/apps/tcr-b2b/register-pro';
      block.appendChild(proLink);
    }

    if (cfg.loggedIn && list.id !== 'local' && cfg.isPro) {
      var quote = el('button', 'wishlist-page__quote btn btn--primary', t('requestQuote', 'Demander un devis pour cette liste'));
      quote.type = 'button';
      quote.addEventListener('click', function () {
        quote.disabled = true;
        quote.textContent = t('sending', 'Envoi…');
        fetch('/apps/tcr-b2b/wishlist-to-quote', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          // locale du storefront → email de devis dans la langue du client
          body: JSON.stringify({
            wishlistId: list.id,
            locale:
              (window.Shopify && window.Shopify.locale) ||
              document.documentElement.lang ||
              '',
          }),
        })
          .then(function (r) {
            return r.json();
          })
          .then(function (res) {
            if (res && res.ok) {
              quote.textContent = t('quoteSent', 'Demande envoyée') + ' — ' + res.draftOrderName;
            } else if (res && res.error === 'not-pro') {
              // Le tag a pu changer depuis le rendu de la page : on renvoie
              // vers l'inscription pro plutôt que d'afficher une erreur sèche.
              window.location.href = cfg.registerProUrl || res.registerUrl;
            } else {
              quote.disabled = false;
              quote.textContent = t('quoteError', 'Échec, réessayer');
            }
          })
          .catch(function () {
            quote.disabled = false;
            quote.textContent = t('quoteError', 'Échec, réessayer');
          });
      });
      block.appendChild(quote);
    }

    root.appendChild(block);

    var handles = list.items.map(function (i) {
      return i.handle;
    });

    return throttled(handles, CONCURRENCY, function (h) {
      return fetchProduct(h);
    }).then(function (products) {
      list.items.forEach(function (item, idx) {
        item.listId = list.id;
        ul.appendChild(renderItem(item, products[idx], allLists));
      });
    });
  }

  function render() {
    var root = document.querySelector('[data-wishlist-page]');
    if (!root || !window.TCRWishlist) return;

    var state = window.TCRWishlist.getState();
    root.innerHTML = '';

    var total = window.TCRWishlist.count();
    if (total === 0) {
      var empty = el('div', 'wishlist-page__empty-state');
      empty.appendChild(el('p', null, t('empty', "Vous n'avez pas encore de favoris.")));
      var cta = el('a', 'btn btn--primary', t('emptyCta', 'Parcourir le catalogue'));
      cta.href = '/collections/all';
      empty.appendChild(cta);
      root.appendChild(empty);
      return;
    }

    if (!state.loggedIn) {
      var notice = el('p', 'wishlist-page__notice', t('loginToSave', 'Connectez-vous pour retrouver vos favoris sur tous vos appareils'));
      var login = el('a', 'wishlist-page__login', t('login', 'Se connecter'));
      login.href = cfg.loginUrl || '/account/login';
      notice.appendChild(document.createTextNode(' '));
      notice.appendChild(login);
      root.appendChild(notice);
    }

    // Création d'une liste nommée. Réservée aux clients connectés : les listes
    // multiples vivent en base, le visiteur anonyme n'en a qu'une en local.
    if (state.loggedIn) {
      var form = el('form', 'wishlist-page__new-list');
      var input = el('input', 'wishlist-page__new-list-input');
      input.type = 'text';
      input.maxLength = 80;
      input.placeholder = t('listName', 'Nom de la liste');
      input.setAttribute('aria-label', t('newList', 'Nouvelle liste'));

      var submit = el('button', 'wishlist-page__new-list-submit', t('newList', 'Nouvelle liste'));
      submit.type = 'submit';

      form.appendChild(input);
      form.appendChild(submit);
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var name = input.value.trim();
        if (!name) return;
        submit.disabled = true;
        window.TCRWishlist.createList(name)
          .then(render)
          .catch(function () {
            submit.disabled = false;
            // Nom déjà pris : la contrainte unique (shop, client, nom) rejette.
            input.setAttribute('aria-invalid', 'true');
          });
      });
      root.appendChild(form);
    }

    state.lists.forEach(function (l) {
      renderList(l, root, state.lists);
    });
  }

  document.addEventListener('tcr:wishlist:change', render);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', render);
  } else {
    render();
  }
})();
